/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Merchant,
  Store,
  getDefaultDisplayCurrencyCode,
  resetDefaultDisplayCurrency,
  resolveDisplayCurrencyCode,
  setDefaultDisplayCurrency,
} from '@bofrak-backend/shared';
import { DefaultValue } from 'recoil';

// The formatter default is one value but its inputs live in two atoms, so
// remember the latest of each and re-resolve whenever either changes. The
// chain stays merchant-first: the store's country only decides when the
// merchant resolves nothing.
let lastMerchant: Merchant | null = null;
let lastStore: Store | null = null;

const applyResolvedCurrency = (): void => {
  // A cached store only participates for its own merchant (or before any
  // merchant is known) — never another account's.
  const store =
    lastStore && (!lastMerchant || lastStore.merchant_id === lastMerchant.id)
      ? lastStore
      : null;
  const resolved = resolveDisplayCurrencyCode({
    merchant: lastMerchant ?? undefined,
    store: store ?? undefined,
  });
  if (resolved) {
    setDefaultDisplayCurrency(resolved);
  } else {
    // Sign-out, or an account that cannot resolve any currency: return to
    // the pre-login default instead of retaining the previous account's.
    resetDefaultDisplayCurrency();
  }
};

const setCurrentMerchant = (merchant: Merchant | null): void => {
  if (merchant === null) {
    // Explicit sign-out/reset also drops the paired store.
    lastStore = null;
  }
  lastMerchant = merchant;
  applyResolvedCurrency();
};

const initialAtomValue = <T>(
  node: any,
  getLoadable: ((n: any) => any) | undefined,
): T | null => {
  // The atom's initialized loadable covers both persisted hydration (an
  // earlier localPersistEffect's setSelf) and RecoilRoot initializeState
  // (which never fires onSet). Deliberately NO localStorage fallback: in a
  // unified deployment several apps share an origin and atom keys, so
  // another app's persisted merchant must never initialize this app's
  // formatter while its own atom is still null.
  try {
    if (getLoadable) {
      const loadable = getLoadable(node);
      const value = loadable?.valueMaybe?.();
      if (value != null) return value as T;
    }
  } catch {
    // not initialized yet — onSet will deliver the value
  }
  return null;
};

/**
 * Recoil atom effect for merchant atoms: keeps the shared money formatters'
 * display currency in sync with the signed-in merchant, so every
 * formatCurrency/formatMoney call without an explicit code shows the
 * merchant's currency (configured currency first, else the currency of the
 * merchant's country) instead of the MZN legacy default. Platform-billing
 * amounts (invoices, wallets) must keep passing their own currency
 * explicitly — this default is merchant money.
 *
 * The effect owns the module state's lifecycle: when its RecoilRoot is
 * disposed (e.g. an EmbeddedReports root inside the admin app), the
 * cleanup restores the display default that was active before the mount,
 * so a viewed merchant's currency never leaks into the host app.
 *
 * Reactivity contract: the module default is DELIBERATELY not reactive
 * state. It is set during atom initialization — before any authenticated
 * screen renders money — and on every merchant/store change, and the
 * screens that display amounts re-render on their own data loads, which
 * follow merchant hydration. A component that renders bare formatter
 * output with no data subscription at all would not repaint on a default
 * change; such a component should pass an explicit currency instead.
 *
 * Attach it AFTER localPersistEffect on the same atom.
 */
export const displayCurrencyEffect = ({ onSet, node, getLoadable }: any) => {
  const defaultBeforeMount = getDefaultDisplayCurrencyCode();

  const initial = initialAtomValue<Merchant>(node, getLoadable);
  if (initial) setCurrentMerchant(initial);

  onSet((merchant: any) => {
    setCurrentMerchant(merchant instanceof DefaultValue ? null : merchant);
  });

  return () => {
    lastMerchant = null;
    lastStore = null;
    setDefaultDisplayCurrency(defaultBeforeMount);
  };
};

/**
 * Companion effect for selected-store atoms: feeds the store's
 * `country_code` into the same resolution, covering merchants whose
 * currency AND country are both unset. A store is only consulted for its
 * own merchant. Attach it to each app's selected-store atom alongside
 * displayCurrencyEffect on the merchant atom.
 */
export const storeDisplayCurrencyEffect = ({
  onSet,
  node,
  getLoadable,
}: any) => {
  const initial = initialAtomValue<Store>(node, getLoadable);
  if (initial) {
    lastStore = initial;
    applyResolvedCurrency();
  }

  onSet((store: any) => {
    lastStore = store instanceof DefaultValue ? null : store;
    applyResolvedCurrency();
  });

  return () => {
    lastStore = null;
  };
};

/**
 * Display symbol for a resolved currency code: MZN's Intl symbol is 'MT'
 * (the legacy symbol merchants know); every other code displays as its own
 * ISO code. Used for labels such as "Amount (MT)".
 */
export const displayCurrencySymbolFor = (code?: string | null): string => {
  const normalized = code?.trim().toUpperCase();
  return normalized === 'MZN' ? 'MT' : normalized ?? '';
};
